<script setup>

import { inject, ref, watch } from 'vue';
import { getErreurFichierRequis } from '@/scripts/controlesSaisie.js';
import { 
	apiImporterFichier } from '@/services/ServiceModele.js';
import Dialog from 'primevue/dialog';
import Formulaire from '@/components/generiques/Formulaire.vue';
import SaisieListe from '@/components/generiques/SaisieListe.vue';
import SaisieFichier from '@/components/generiques/SaisieFichier.vue';

const { propositionImport } = defineProps({
    propositionImport: Object
});

const visible = defineModel('visible', {
    type: Boolean
});

const schema = inject('schema');
const dernierFichierSelectionne = inject('dernierFichierSelectionne');
const evenement = inject('evenement');
const appelerApiModifModele = inject('appelerApiModifModele');
const afficherSucces = inject('afficherSucces');
const afficherInfo = inject('afficherInfo');

const importFichier = ref(getImportInitial());
const ligneEntete = ref(null);
const importsCompatibles = ref([]);

watch(() => visible.value, (visible) => {
    if (visible) {
        importFichier.value.fichier = dernierFichierSelectionne.value;
        importFichier.value.importDonnees = propositionImport;
    }
});

watch(() => importFichier.value.fichier, (fichier) => {
    if (fichier !== null) {
        dernierFichierSelectionne.value = fichier;
        lireLigneEntete();
    } else {
        ligneEntete.value = null;
    }
});

watch(() => ligneEntete.value, () => {
    rafraichirImportsCompatibles();
});

watch(() => importFichier.value.importDonnees, (importDonnees) => {
    if (importDonnees?.id === 0) {
        visible.value = false;
        evenement.value.nouveauModeleImport = true;
        importFichier.value.fichier = null;
        afficherInfo('Création d\'un nouveau modèle d\'import...');
    }
});

function getImportInitial() {
    return {
        fichier: dernierFichierSelectionne.value,
        importDonnees: propositionImport
    };
}

function estImportCompatibleAvecEntete(importDonnees, ligneEntete) {
    const colonnesEntete = ligneEntete.split(importDonnees.delimiteur);
    return importDonnees.nomsColonnes.every(nomCol => colonnesEntete.includes(nomCol));
}

function lireLigneEntete() {
    const fileReader = new FileReader();
    fileReader.onload = function() {
        const contenuFichier = fileReader.result.replaceAll('\r', '');
        const indiceRetourLigne = contenuFichier.indexOf('\n');
        if (indiceRetourLigne != -1) {
            ligneEntete.value = contenuFichier.substring(0, indiceRetourLigne);
        } else {
            ligneEntete.value = contenuFichier;
        }
    };
    fileReader.readAsText(importFichier.value.fichier);
}

function rafraichirImportsCompatibles() {
    if (ligneEntete.value === null) {
        importsCompatibles.value = [];
        importFichier.value.importDonnees = null;
    } else {
        importsCompatibles.value = schema.value.imports.filter(imp => estImportCompatibleAvecEntete(imp, ligneEntete.value));
        if (importsCompatibles.value.length > 0) {
            if (!importFichier.value.importDonnees || !importsCompatibles.value.includes(importFichier.value.importDonnees)) {
                importFichier.value.importDonnees = importsCompatibles.value[0];
            }
        } else {
            importFichier.value.importDonnees = null;
        }
        importsCompatibles.value.push({id: 0, libelle: 'Nouveau modèle d\'import...'});
    }
}

function validerSaisie(importFichier) {
    const erreurs = {};
    erreurs.fichier = getErreurFichierRequis(importFichier.fichier);
    if (!importFichier.importDonnees?.id) {
        erreurs.importDonnees = 'Un modèle d\'import doit être sélectionné';
    } else {
        erreurs.importDonnees = null;
    }
    return erreurs;
}

function validerFormulaire() {
    importFichier.value.validationFormulaire = true;
}

function importerFichier() {
    appelerApiModifModele(apiImporterFichier, importFichier.value.importDonnees.id, importFichier.value.fichier, (reponseOK) => {
        visible.value = false;
        importFichier.value.fichier = null;
        importFichier.value.importDonnees = null;
        if (reponseOK) {
            afficherSucces('Import terminé');
            dernierFichierSelectionne.value = null;
        }
    });
}

</script>

<template>
    <Dialog 
        v-model:visible="visible" 
        modal 
        :header="'Import de fichier'">
            <Formulaire
                :objet="importFichier"
                :fonction-validation-saisie="validerSaisie"
                @validation="importerFichier">
                <label class="intitule">Fichier :</label>
                <SaisieFichier v-model:valeur="importFichier.fichier" :id-erreur="'fichier'" />
                <label class="intitule">Modèle d'import :</label>
                <SaisieListe 
                    v-model:valeur="importFichier.importDonnees" 
                    :id-erreur="'importDonnees'" 
                    :options="importsCompatibles" 
                    :optionLabel="'libelle'" 
                    :placeholder="'Choisir un modèle d\'import'"
                    :emptyMessage="importFichier.fichier ? 'Aucun modèle d\'import compatible' : 'Aucun fichier sélectionné'" />
            </Formulaire>
            <template #footer>
                <button 
                    class="bouton fas fa-check btn-primaire" 
                    @click="validerFormulaire()"/>
            </template>
    </Dialog>
</template>

<style scoped>

</style>